// src/pages/paidProject/Annexure 1/BreakEvenPointAnalysis.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

// ─── Depreciation helper from Depreciation.jsx ─────────────
function calcFullYear(cost, rate, yearIndex) {
  if (yearIndex === 0) return 0;
  let remaining = cost,
    dep = 0;
  for (let i = 1; i <= yearIndex; i++) {
    dep = (remaining * rate) / 100;
    remaining -= dep;
  }
  return dep;
}

function generateMonthLabels(startStr, length) {
  if (!startStr || !startStr.includes("-")) return [];
  const [startM, startY] = startStr.split("-").map(Number);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let m = startM - 1,
    y = startY;
  return Array.from({ length }, () => {
    const label = `${months[m]}-${y}`;
    m = (m + 1) % 12;
    if (m === 0) y++;
    return label;
  });
}

export default function BreakEvenPointAnalysis() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);

  // 1) Load from API
  useEffect(() => {
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => setData(data))
      .catch((err) => console.error("Failed to load data", err));
  }, [projectId]);

  // 2) Destructure safely
  const {
    projectYears = [],
    capacityUtilizationPerYear = [],
    rmConsumptionInputs = [],
    annualSalesRealisationInputs = [],
    manufacturedProducts = [],
    depreciationData = [],
    termLoanInput = {},
    projectedProfitabilityInputs = {},
    breakEvenAnalysisInputs = {},
    totalAnnualSales: totalAnnualSalesLS = [],
    grandTotalCostOfProduction: grandTotalsLS = [],
    averageCostOfProdPerMT: avgCostPerMTPerYearLS = [],
    totalClosingStock: closingStockPerYearLS = [],
    emiSchedule,
  } = data || {};

  const getCostByParticular = useCallback(
    (p) => {
      if (!data) return 0;
      const {
        costProjectInputs = {},
        plantMachineryData = [],
        plantContingencies,
        plantGstPercent,
        furnitureAndFixtures = [],
        otherFixedAssets = [],
        computers = [],
        preliminaryExpenses = [],
      } = data;
      const keyMap = {
        "Land & Site Development (On Lease)": "landDevelopment",
        "Civil Construction (On Lease)": "civilConstruction",
      };
      if (keyMap[p] && costProjectInputs[keyMap[p]] != null) {
        return parseFloat(costProjectInputs[keyMap[p]]) || 0;
      }
      switch (p) {
        case "Plant & Machinery": {
          const subtotal = plantMachineryData.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
          const cont = (subtotal * (parseFloat(plantContingencies) || 0)) / 100;
          const tax =
            ((subtotal + cont) * (parseFloat(plantGstPercent) || 0)) / 100;
          return subtotal + cont + tax;
        }
        case "Furniture and Fixture":
          return furnitureAndFixtures.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        case "Miscellaneous Fixed Assets":
          return otherFixedAssets.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        case "Computer and Software":
          return computers.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        case "Preliminary Expenses":
          return preliminaryExpenses.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        default:
          return 0;
      }
    },
    [data]
  );

  // Simple inputs
  const leaseRent = +projectedProfitabilityInputs.leaseRent || 0;
  const sellingAndDistributionExpense =
    +projectedProfitabilityInputs.sellingAndDistributionExpense || 0;
  const prelimWrittenOff =
    +projectedProfitabilityInputs.preliminaryExpensesWrittenOff || 0;
  const sellExpensePct = +breakEvenAnalysisInputs.sellingExpense || 0;
  const fixedSellExpensePct = +breakEvenAnalysisInputs.fixedSellingExpense || 0;

  // Helpers
  const fmt = (n) =>
    isNaN(n) ? "-" : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  // Parse start-month
  const prodStart = useMemo(() => {
    const [m, y] = (termLoanInput.startMonth || "").split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  // Months active
  const getMonthsActive = useCallback(
    (fy) => {
      if (!prodStart) return 12;
      const s = parseInt(fy, 10),
        e = s + 1;
      const raw = (e - prodStart.year) * 12 + (3 - prodStart.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prodStart]
  );

  // Installed & production (unused here but kept for completeness)
  const installedArr = useMemo(
    () =>
      rmConsumptionInputs.map((r) => {
        const d = +r.workingDays || 0,
          h = +r.workingHoursPerDay || 0,
          o = +r.outputPerHour || 0;
        return (d * h * o) / 1000;
      }),
    [rmConsumptionInputs]
  );
  const totalInstalled = useMemo(
    () => installedArr.reduce((s, v) => s + v, 0),
    [installedArr]
  );
  const productionMT = useMemo(
    () =>
      projectYears.map((fy, i) => {
        const util = +capacityUtilizationPerYear[i] || 0;
        const m = getMonthsActive(fy);
        return +((totalInstalled * util * m) / (100 * 12)).toFixed(2);
      }),
    [projectYears, capacityUtilizationPerYear, totalInstalled, getMonthsActive]
  );

  // A+B GRAND TOTAL is now read from localStorage
  const grandTot = grandTotalsLS;

  // 1) Compute total closing quantity (MT) per year exactly as AnnualSalesRealisation did:
  const totalClosingQty = useMemo(() => {
    // avg working days
    const avgWD = rmConsumptionInputs.length
      ? rmConsumptionInputs.reduce((s, r) => s + (+r.workingDays || 0), 0) /
        rmConsumptionInputs.length
      : 0;

    // inventory days per product
    const invDaysMap = Object.fromEntries(
      annualSalesRealisationInputs.map((s) => [
        s.product,
        +s.inventoryDays || 0,
      ])
    );

    // installed capacity per product
    const installedByProd = manufacturedProducts.reduce((m, { product }) => {
      m[product] = rmConsumptionInputs
        .filter((r) => r.products === product)
        .reduce((sum, r) => {
          const d = +r.workingDays || 0;
          const h = +r.workingHoursPerDay || 0;
          const o = +r.outputPerHour || 0;
          return sum + (d * h * o) / 1000;
        }, 0);
      return m;
    }, {});

    // production MT per product per FY
    const prodByProd = {};
    projectYears.forEach((fy, yi) => {
      const util = +capacityUtilizationPerYear[yi] || 0;
      const months = getMonthsActive(fy);
      manufacturedProducts.forEach(({ product }) => {
        const inst = installedByProd[product] || 0;
        const mt = (inst * util * months) / (100 * 12);
        prodByProd[product] = prodByProd[product] || [];
        prodByProd[product][yi] = mt;
      });
    });

    // closing qty per year = sum over products of (mt / avgWD * invDays)
    return projectYears.map((_, yi) => {
      return manufacturedProducts.reduce((sum, { product }) => {
        const mt = prodByProd[product]?.[yi] || 0;
        const inv = invDaysMap[product] || 0;
        const closing = avgWD ? (mt / avgWD) * inv : 0;
        return sum + closing;
      }, 0);
    });
  }, [
    rmConsumptionInputs,
    annualSalesRealisationInputs,
    manufacturedProducts,
    capacityUtilizationPerYear,
    projectYears,
    getMonthsActive,
  ]);

  // 2) Compute closing stock in lacs exactly as ProjectedProfitability does
  const closingStockFG = useMemo(
    () =>
      closingStockPerYearLS.map((cs, i) => {
        const avgC = Number(avgCostPerMTPerYearLS[i]) || 0;
        return +(((Number(cs) || 0) * avgC) / 100000).toFixed(2);
      }),
    [closingStockPerYearLS, avgCostPerMTPerYearLS]
  );

  // 3) Opening stock is last year’s closing
  const openingStockFG = useMemo(
    () => closingStockFG.map((_, i) => (i > 0 ? closingStockFG[i - 1] : 0)),
    [closingStockFG]
  );

  // 4) Increase / Decrease = Opening − Closing
  const incDecStock = useMemo(
    () =>
      openingStockFG.map(
        (open, i) => +(open - closingStockFG[i] ?? 0).toFixed(2)
      ),
    [openingStockFG, closingStockFG]
  );
  // Selling Expenses from SG&A * sellExpensePct
  const sgaExpenses = useMemo(() => {
    return totalAnnualSalesLS.map(
      (sales) => +((sales * sellingAndDistributionExpense) / 100).toFixed(2)
    );
  }, [totalAnnualSalesLS, sellingAndDistributionExpense]);

  // 2) Selling Expenses for the break-even page = SG&A × sellExpensePct%
  const sellingExp = useMemo(() => {
    return sgaExpenses.map((sg) => +((sg * sellExpensePct) / 100).toFixed(2));
  }, [sgaExpenses, sellExpensePct]);

  // Total Costs & Contribution
  const totalCosts = projectYears.map(
    (_, i) => +(grandTot[i] + incDecStock[i] + sellingExp[i]).toFixed(2)
  );
  const contribution = projectYears.map(
    (_, i) => +(totalAnnualSalesLS[i] - totalCosts[i]).toFixed(2)
  );

  // Fixed Selling Expenses
  const fixedSellingExp = sgaExpenses.map(
    (v) => +((v * fixedSellExpensePct) / 100).toFixed(2)
  );

  // Lease & Prelim arrays
  const leaseArr = projectYears.map((_, i) => (i > 0 ? leaseRent : 0));
  const prelimArr = projectYears.map((_, i) => (i > 0 ? prelimWrittenOff : 0));

  // Term Loan Charges placeholder
  // Pull emiSchedule & start-month from your API data
  const term = data?.termLoanInput || {};
  const rawStart =
    term.pStartMonth && term.pStartMonth.includes("-")
      ? term.pStartMonth
      : term.startMonth || "";

  const financeSummary = useMemo(() => {
    let emi = Array.isArray(term.emiSchedule) ? term.emiSchedule : [];

    // fallback to localStorage
    if (!emi.length) {
      const local = emiSchedule || "[]";
      if (Array.isArray(local) && local.length) emi = local;
    }
    if (!emi.length || !rawStart) return [];

    const monthLabels = generateMonthLabels(rawStart, emi.length);
    const monthsMap = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };
    const bucket = {};
    emi.forEach((row, idx) => {
      const lbl = monthLabels[idx] || "";
      const [monStr, yrStr] = lbl.split("-");
      const mon = monthsMap[monStr] || 0;
      const yr = parseInt(yrStr, 10) || 0;
      const fyStart = mon >= 4 ? yr : yr - 1;
      const fyLabel = `${fyStart}-${String(fyStart + 1).slice(-2)}`;

      if (!bucket[fyLabel]) {
        bucket[fyLabel] = {
          year: fyLabel,
          totalPrincipal: 0,
          totalInterest: 0,
          closingBalance: 0,
        };
      }
      bucket[fyLabel].totalPrincipal += row.principal || 0;
      bucket[fyLabel].totalInterest += row.interest || 0;
      bucket[fyLabel].closingBalance = row.closingBalance || 0;
    });
    return Object.values(bucket);
  }, [term.emiSchedule, term.startMonth, term.pStartMonth]);

  const termLoanCharges = useMemo(
    () => financeSummary.map((fy) => +(fy.totalInterest || 0)),
    [financeSummary]
  );

  const depreciationTotals = useMemo(() => {
    return projectYears.map((fy, yi) => {
      const monthlyFactor = getMonthsActive(fy) / 12;
      return depreciationData.reduce((sum, row) => {
        const cost = getCostByParticular(row.particular);
        const fullDep = calcFullYear(cost, row.taxPercent, yi);
        return sum + fullDep * monthlyFactor;
      }, 0);
    });
  }, [projectYears, depreciationData, getCostByParticular, getMonthsActive]);

  // Fixed costs total
  const fixedCostsTotals = projectYears.map(
    (_, i) =>
      +(
        fixedSellingExp[i] +
        (depreciationTotals[i] || 0) +
        termLoanCharges[i] +
        leaseArr[i] +
        prelimArr[i]
      ).toFixed(2)
  );

  // Profit, Margin, InValue, InPercentage
  const profit = projectYears.map(
    (_, i) => +(contribution[i] - fixedCostsTotals[i]).toFixed(2)
  );
  const marginPct = projectYears.map((_, i) =>
    totalAnnualSalesLS[i] > 0
      ? +((contribution[i] / totalAnnualSalesLS[i]) * 100).toFixed(2)
      : 0
  );
  const inValues = projectYears.map((_, i) =>
    marginPct[i] > 0
      ? +((fixedCostsTotals[i] * 100) / marginPct[i]).toFixed(2)
      : 0
  );
  const inPercentages = projectYears.map((_, i) =>
    totalAnnualSalesLS[i] > 0
      ? +((inValues[i] / totalAnnualSalesLS[i]) * 100).toFixed(2)
      : 0
  );

  // 3) Loading guard
  if (!data) {
    return <div className="p-6 text-center">Loading…</div>;
  }

  // 4) Render
  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-bold">Break-Even Point Analysis</h1>

      {/* Main Analysis */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Details</th>
              {projectYears.map((yr, i) => (
                <th key={i} className="p-2 text-center">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-2">Sales</td>
              {totalAnnualSalesLS.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="p-2">Cost of Production (A+B)</td>
              {grandTot.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-2">Increase/Decrease in Stock</td>
              {incDecStock.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="p-2">Selling Expenses</td>
              {sellingExp.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="font-semibold bg-gray-200">
              <td className="p-2">Total</td>
              {totalCosts.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="font-semibold bg-gray-200">
              <td className="p-2">Contribution (A−B)</td>
              {contribution.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Fixed Costs */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Details</th>
              {projectYears.map((yr, i) => (
                <th key={i} className="p-2 text-center">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-2">Fixed Selling Expense</td>
              {fixedSellingExp.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="p-2">Depreciation</td>
              {depreciationTotals.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-2">Financial Charges (Term Loan)</td>
              {termLoanCharges.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="p-2">Lease Rent</td>
              {leaseArr.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-2">Prelim. Expenses Written Off</td>
              {prelimArr.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="font-semibold bg-gray-200">
              <td className="p-2">Total</td>
              {fixedCostsTotals.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="font-semibold bg-gray-200">
              <td className="p-2">PROFIT (C−D)</td>
              {profit.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Contribution Margin & Break-Even */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Details</th>
              {projectYears.map((yr, i) => (
                <th key={i} className="p-2 text-center">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-2">P/V Ratio (%)</td>
              {marginPct.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="p-2">IN VALUE</td>
              {inValues.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="p-2">IN PERCENTAGE</td>
              {inPercentages.map((v, i) => (
                <td key={i} className="p-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
